import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import Cookies from "js-cookie";
import toast from "react-hot-toast";
import { Copy, X, Trash2, MessageSquare, Sparkles, Forward } from "lucide-react";

import MessageCard from "./MessageCard";
import CreateMessage from "./addMessageToChannel";
import ForwardMessageDialog from "./ForwardMessageDialog";

import { BACKEND_API_URL } from "../utils/api";
import {
  saveLastViewedChannel,
  setFullscreenImage,
} from "../redux/actions/channel.action";
import { selectActiveThreadParentId } from "../redux/selectors/channel.selectors";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";

const REACT_APP_BACKEND_API_URL = BACKEND_API_URL;

const MessageList = () => {
  const { channelId } = useParams();
  const location = useLocation();
  const messageId = new URLSearchParams(location.search).get('messageId');
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const activeThreadParentId = useSelector(selectActiveThreadParentId);
  const mobileScreen = window?.innerWidth <= 600;

  // Local state for messages
  const [messages, setMessages] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const containerRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [selectedMessages, setSelectedMessages] = useState([]);
  const [focusedMessageIndex, setFocusedMessageIndex] = useState(-1);
  const messageRefs = useRef({});
  const lastMessageIdRef = useRef(null);
  const previousMessageCountRef = useRef(0);
  const lastSelectedIndexRef = useRef(null);
  const [forwardDialogOpen, setForwardDialogOpen] = useState(false);

  // Parse messageId from URL for highlighting
  const selectedMessageId = messageId ? parseInt(messageId) : null;

  // Save last viewed channel
  useEffect(() => {
    if (channelId) {
      dispatch(saveLastViewedChannel(channelId));
    }
  }, [channelId, dispatch]);

  // Reset state when channel changes
  useEffect(() => {
    setPage(1);
    setMessages([]);
    setHasMore(true);
    setIsInitialLoad(true);
    setSelectedMessages([]);
    setFocusedMessageIndex(-1);
    lastMessageIdRef.current = null;
    lastSelectedIndexRef.current = null;
  }, [channelId]);

  // Fetch messages
  useEffect(() => {
    const fetchMessages = async (pageNum) => {
      if (!channelId) return;

      try {
        setIsLoadingMore(true);
        const accessToken = Cookies.get("access_token");
        const response = await axios.get(
          `${REACT_APP_BACKEND_API_URL}/api/channels/${channelId}/messages`,
          {
            headers: {
              Authorization: `Token ${accessToken}`,
              "Content-Type": "application/json",
            },
            params: { page: pageNum },
          }
        );

        if (response.data && response.data.results) {
          const reversedResults = [...response.data.results].reverse();

          if (pageNum === 1) {
            setMessages(reversedResults);
            setIsInitialLoad(true);
            if (reversedResults.length > 0) {
              lastMessageIdRef.current = Math.max(
                ...reversedResults.map((m) => m.id)
              );
            }
          } else {
            const scrollElement = scrollContainerRef.current;
            const previousScrollHeight = scrollElement?.scrollHeight || 0;

            setMessages((prevMessages) => [
              ...reversedResults,
              ...prevMessages,
            ]);

            setTimeout(() => {
              if (scrollElement) {
                const newScrollHeight = scrollElement.scrollHeight;
                scrollElement.scrollTop =
                  newScrollHeight - previousScrollHeight;
              }
            }, 0);
          }

          if (response.data?.next === null) {
            setHasMore(false);
          }
        }
      } catch (error) {
        console.error("Failed to fetch messages:", error);
      } finally {
        setIsLoadingMore(false);
      }
    };

    fetchMessages(page);
  }, [page, channelId]);

  // Poll for new messages
  useEffect(() => {
    if (!channelId) return;

    const pollForNewMessages = async () => {
      try {
        const accessToken = Cookies.get("access_token");
        const params = { page: 1 };
        if (lastMessageIdRef.current) {
          params.since_id = lastMessageIdRef.current;
        }

        const response = await axios.get(
          `${REACT_APP_BACKEND_API_URL}/api/channels/${channelId}/messages`,
          {
            headers: {
              Authorization: `Token ${accessToken}`,
              "Content-Type": "application/json",
            },
            params,
          }
        );

        if (response.data && response.data.results) {
          const reversedResults = [...response.data.results].reverse();
          if (reversedResults.length > 0) {
            setMessages((prev) => {
              const existingIds = new Set(prev.map((m) => m.id));
              const newMessages = reversedResults.filter(
                (m) => !existingIds.has(m.id)
              );
              if (newMessages.length > 0) {
                const updated = [...prev, ...newMessages];
                lastMessageIdRef.current = Math.max(
                  ...updated.map((m) => m.id)
                );
                return updated;
              }
              return prev;
            });
          }
        }
      } catch (error) {
        console.error("Failed to poll messages:", error);
      }
    };

    const interval = setInterval(pollForNewMessages, 1000);
    return () => clearInterval(interval);
  }, [channelId]);

  // Auto-scroll to bottom (skip if we have a selectedMessageId to scroll to)
  useEffect(() => {
    if (messages.length > 0 && scrollContainerRef.current) {
      const scrollElement = scrollContainerRef.current;

      if (isInitialLoad) {
        // If we have a selectedMessageId, scroll to that message instead of bottom
        if (selectedMessageId && messageRefs.current[selectedMessageId]) {
          setTimeout(() => {
            messageRefs.current[selectedMessageId].scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
            setIsInitialLoad(false);
          }, 100);
        } else {
          setTimeout(() => {
            scrollElement.scrollTop = scrollElement.scrollHeight;
            setIsInitialLoad(false);
          }, 100);
        }
      } else if (!isLoadingMore) {
        const isNewMessage = messages.length > previousMessageCountRef.current;
        if (isNewMessage) {
          setTimeout(() => {
            scrollElement.scrollTop = scrollElement.scrollHeight;
          }, 50);
        }
      }

      previousMessageCountRef.current = messages.length;
    }
  }, [messages, isInitialLoad, isLoadingMore, selectedMessageId]);

  // Handle scroll for pagination
  const handleScroll = (e) => {
    const scrollElement = e.target;
    if (
      scrollElement.scrollTop < 50 &&
      hasMore &&
      !isLoadingMore &&
      !isInitialLoad
    ) {
      setPage((prevPage) => prevPage + 1);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Skip if user is typing in an input element
      const tag = e.target.tagName.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || e.target.isContentEditable) {
        return;
      }

      if (e.key === "Escape") {
        e.preventDefault();
        setFocusedMessageIndex(-1);
        setSelectedMessages([]);
        lastSelectedIndexRef.current = null;
        return;
      }

      if (messages.length === 0) return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setFocusedMessageIndex((prev) => {
          const nextIndex = prev < messages.length - 1 ? prev + 1 : prev;
          setTimeout(() => {
            const messageId = messages[nextIndex]?.id;
            if (messageId && messageRefs.current[messageId]) {
              messageRefs.current[messageId].scrollIntoView({
                behavior: "smooth",
                block: "center",
              });
            }
          }, 0);
          return nextIndex;
        });
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setFocusedMessageIndex((prev) => {
          const nextIndex = prev > 0 ? prev - 1 : 0;
          setTimeout(() => {
            const messageId = messages[nextIndex]?.id;
            if (messageId && messageRefs.current[messageId]) {
              messageRefs.current[messageId].scrollIntoView({
                behavior: "smooth",
                block: "center",
              });
            }
          }, 0);
          return nextIndex;
        });
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [messages]);

  // Thread navigation
  const handleOpenThread = (threadId, parentMessageId) => {
    // Navigate if we have a valid message ID (threadId can be -1 for new threads)
    if (parentMessageId != null) {
      navigate(`/channels/${channelId}?messageId=${parentMessageId}`);
    }
  };

  const updateMessageStatus = (msgId) => {
    const messageIndex = messages.findIndex((message) => message.id === msgId);
    if (messageIndex !== -1) {
      const updatedMessages = [...messages];
      updatedMessages[messageIndex] = {
        ...updatedMessages[messageIndex],
        action_taken: true,
      };
      setMessages(updatedMessages);
    }
  };

  const handleDeleteMessage = (msgId) => {
    setMessages((prevMessages) =>
      prevMessages.filter((msg) => msg.id !== msgId)
    );
    setSelectedMessages((prev) => prev.filter((msg) => msg.id !== msgId));
  };

  const handleMessageSelect = (message, event) => {
    if (event?.ctrlKey || event?.metaKey) {
      const currentIndex = messages.findIndex((m) => m.id === message.id);

      if (event?.shiftKey && lastSelectedIndexRef.current !== null) {
        // Bulk select: select all messages between last selected and current
        const start = Math.min(lastSelectedIndexRef.current, currentIndex);
        const end = Math.max(lastSelectedIndexRef.current, currentIndex);
        const rangeMessages = messages.slice(start, end + 1);

        setSelectedMessages((prev) => {
          const existingIds = new Set(prev.map((m) => m.id));
          const newMessages = rangeMessages.filter((m) => !existingIds.has(m.id));
          const totalAfterAdd = prev.length + newMessages.length;

          // Show popup only when transitioning from 30 to 31+ messages
          if (prev.length <= 30 && totalAfterAdd > 30) {
            toast.error(`Maximum limit of 30 messages for forwarding reached. You can forward up to 30 messages at once.`);
          }

          return [...prev, ...newMessages];
        });
      } else {
        // Individual toggle (existing behavior)
        setSelectedMessages((prev) => {
          const isAlreadySelected = prev.some((msg) => msg.id === message.id);
          if (isAlreadySelected) {
            return prev.filter((msg) => msg.id !== message.id);
          } else {
            // Show popup only when transitioning from exactly 30 to 31 messages
            if (prev.length === 30) {
              toast.error(`Maximum limit of 30 messages for forwarding reached. You can forward up to 30 messages at once.`);
            }
            return [...prev, message];
          }
        });
        // Update last selected index for future range selections
        lastSelectedIndexRef.current = currentIndex;
      }
    }
  };

  const handleClearSelection = () => {
    setSelectedMessages([]);
  };

  const handleForwardClick = () => {
    if (selectedMessages.length > 30) {
      toast.error('Maximum 30 messages can be forwarded at once');
      return;
    }
    setForwardDialogOpen(true);
  };

  const handleCopySelectedToCanvas = () => {
    if (selectedMessages.length === 0) return;

    // In production, use hardcoded URL for media files served from onemap.morphlelabs.com
    const PRODUCTION_MEDIA_URL = 'https://onemap.morphlelabs.com';

    const messagesData = selectedMessages.map((message) => ({
      type: "channel_message",
      media_type: message.media_type,
      media_url: message.media_url?.startsWith("/")
        ? `${PRODUCTION_MEDIA_URL}${message.media_url}`
        : message.media_url,
      message_text: message.message || "",
      author: `${message.author.first_name} ${message.author.last_name}`,
      created_at: message.created_at,
      channel_message_id: message.id,
    }));

    localStorage.setItem(
      "copiedChannelMessages",
      JSON.stringify({
        messages: messagesData,
        timestamp: new Date().getTime(),
      })
    );

    toast.success(
      `${selectedMessages.length} message(s) copied! Press Ctrl+V in folder canvas to create items`
    );
    setSelectedMessages([]);
  };

  const handleDeleteSelected = async () => {
    if (selectedMessages.length === 0) return;

    const confirmDelete = window.confirm(
      `Delete ${selectedMessages.length} message(s)? This action cannot be undone.`
    );
    if (!confirmDelete) return;

    const accessToken = Cookies.get("access_token");
    let successCount = 0;

    for (const message of selectedMessages) {
      try {
        await axios.delete(
          `${REACT_APP_BACKEND_API_URL}/api/message/${message.id}/delete/`,
          {
            headers: {
              Authorization: `Token ${accessToken}`,
              "Content-Type": "application/json",
            },
          }
        );
        successCount++;
      } catch (error) {
        console.error(`Failed to delete message ${message.id}:`, error);
      }
    }

    if (successCount > 0) {
      const deletedIds = selectedMessages
        .slice(0, successCount)
        .map((m) => m.id);
      setMessages((prevMessages) =>
        prevMessages.filter((msg) => !deletedIds.includes(msg.id))
      );
      toast.success(
        `Deleted ${successCount} of ${selectedMessages.length} message(s)`
      );
    } else {
      toast.error("Failed to delete messages");
    }

    setSelectedMessages([]);
  };

  // Compare calendar days using toDateString() so we match formatDateSeparator (Today/Yesterday) and avoid timezone/parse issues
  const isDifferentDay = (date1, date2) => {
    if (date1 == null || date2 == null) return true;
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    if (Number.isNaN(d1.getTime()) || Number.isNaN(d2.getTime())) return true;
    return d1.toDateString() !== d2.toDateString();
  };

  const formatDateSeparator = (date) => {
    const msgDate = new Date(date);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (msgDate.toDateString() === today.toDateString()) {
      return "Today";
    } else if (msgDate.toDateString() === yesterday.toDateString()) {
      return "Yesterday";
    } else {
      return msgDate.toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    }
  };

  const shouldShowAuthor = (currentMessage, previousMessage) => {
    if (!previousMessage) return true;
    if (currentMessage.author.id !== previousMessage.author.id) return true;
    if (isDifferentDay(previousMessage.created_at, currentMessage.created_at))
      return true;
    return false;
  };

  const isLastInSeries = (currentMessage, nextMessage) => {
    if (!nextMessage) return true;
    if (currentMessage.author.id !== nextMessage.author.id) return true;
    if (isDifferentDay(currentMessage.created_at, nextMessage.created_at))
      return true;
    return false;
  };

  const handleMessageFocus = (message) => {
    const index = messages.findIndex((msg) => msg.id === message.id);
    if (index !== -1) {
      setFocusedMessageIndex(index);
    }
  };

  // Callback to add a new message and update lastMessageIdRef
  // This prevents race conditions with the polling mechanism
  const addMessage = (newMessage) => {
    setMessages((prevMessages) => {
      // Check if message already exists to prevent duplicates
      if (prevMessages.some((m) => m.id === newMessage.id)) {
        return prevMessages;
      }
      return [...prevMessages, newMessage];
    });
    // Update the ref so polling won't fetch this message again
    if (newMessage.id > (lastMessageIdRef.current || 0)) {
      lastMessageIdRef.current = newMessage.id;
    }
  };

  // Date separator component
  const DateSeparator = ({ date }) => (
    <div className="flex items-center gap-3 my-5">
      <Separator className="flex-1" />
      <span className="text-xs font-semibold text-primary bg-background px-3 py-1 rounded-full border border-primary/30">
        {formatDateSeparator(date)}
      </span>
      <Separator className="flex-1" />
    </div>
  );

  return (
    <div className="h-full w-full flex flex-col">
      {/* Messages container */}
      <div
        className={cn(
          "flex-1 min-h-0 w-full overflow-hidden",
          !mobileScreen && "pl-2 pr-2"
        )}
      >
        {channelId ? (
          <div
            ref={scrollContainerRef}
            onScroll={handleScroll}
            className={cn(
              "h-full w-full overflow-auto relative",
              "bg-muted/50 dark:bg-muted/30",
              mobileScreen ? "pb-32" : "rounded-lg"
            )}
          >
            {/* Loading older messages indicator */}
            {isLoadingMore && hasMore && (
              <div className="text-center py-3">
                <span className="text-sm text-muted-foreground">
                  Loading older messages...
                </span>
              </div>
            )}

            {/* No more messages indicator */}
            {!hasMore && messages.length > 0 && !isInitialLoad && (
              <div className="text-center py-3 mb-3">
                <span className="text-sm text-muted-foreground italic">
                  No more messages before this
                </span>
              </div>
            )}
            {/* Empty state */}
            {messages.length === 0 && !isLoadingMore && (
              <div className="h-full flex items-center justify-center p-8">
                <div className="text-center max-w-md space-y-4">
                  <div className="flex justify-center">
                    <div className="rounded-full bg-primary/10 p-6">
                      <MessageSquare className="h-12 w-12 text-primary" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-xl font-semibold">No messages yet</h3>
                    <p className="text-sm text-muted-foreground">
                      Be the first to start the conversation in this channel.
                      Share your thoughts, ideas, or just say hello!
                    </p>
                  </div>
                  <div className="pt-2">
                    <Badge variant="outline" className="gap-2">
                      <Sparkles className="h-3 w-3" />
                      Start typing below to send a message
                    </Badge>
                  </div>
                </div>
              </div>
            )}
            {/* Messages */}
            {messages.map((message, index) => (
              <React.Fragment key={message.id}>
                {/* Date separator */}
                {(index === 0 ||
                  isDifferentDay(
                    messages[index - 1].created_at,
                    message.created_at
                  )) && <DateSeparator date={message.created_at} />}

                {/* Message card */}
                <div
                  ref={(el) => {
                    messageRefs.current[message.id] = el;
                  }}
                >
                  <MessageCard
                    message={message}
                    isThread={false}
                    prepareActiveThread={handleOpenThread}
                    updateMessageStatus={updateMessageStatus}
                    openFullscreenImage={(url) =>
                      dispatch(setFullscreenImage(url))
                    }
                    user={user}
                    selectedMessageId={selectedMessageId}
                    onDelete={handleDeleteMessage}
                    isSelected={selectedMessages.some(
                      (msg) => msg.id === message.id
                    )}
                    isActiveThread={false}
                    isThreadOpen={activeThreadParentId !== null && parseInt(message.id) === activeThreadParentId}
                    onSelect={handleMessageSelect}
                    showAuthor={shouldShowAuthor(
                      message,
                      index > 0 ? messages[index - 1] : null
                    )}
                    showAvatar={isLastInSeries(
                      message,
                      index < messages.length - 1 ? messages[index + 1] : null
                    )}
                    onFocus={handleMessageFocus}
                    isFocused={focusedMessageIndex === index}
                    mobileScreen={mobileScreen}
                  />
                </div>
              </React.Fragment>
            ))}

            {/* Multi-select toolbar */}
            {selectedMessages.length > 0 && (
              <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-50 bg-card border rounded-lg shadow-lg p-3 flex items-center gap-3">
                <Badge variant="default" className="text-sm">
                  {selectedMessages.length} selected
                </Badge>
                <Button
                  size="sm"
                  onClick={handleCopySelectedToCanvas}
                  className="gap-2"
                >
                  <Copy className="h-4 w-4" />
                  Link to Canvas
                </Button>
                <Button
                  size="sm"
                  onClick={handleForwardClick}
                  className="gap-2"
                  disabled={selectedMessages.length > 30}
                >
                  <Forward className="h-4 w-4" />
                  Forward
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={handleDeleteSelected}
                  className="gap-2"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleClearSelection}
                  className="gap-2"
                >
                  <X className="h-4 w-4" />
                  Clear
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="h-full flex items-center justify-center">
            <span className="text-muted-foreground">
              Select a channel to load messages
            </span>
          </div>
        )}
      </div>

      {/* Message composer */}
      <div className="shrink-0">
        <CreateMessage
          messages={messages}
          addMessage={addMessage}
          channelId={channelId}
          threadId={null}
          parentMessageId={null}
        />
      </div>

      {/* Forward Message Dialog */}
      <ForwardMessageDialog
        open={forwardDialogOpen}
        onOpenChange={setForwardDialogOpen}
        selectedMessages={selectedMessages}
        onForwardSuccess={() => {
          setSelectedMessages([]);
        }}
      />
    </div>
  );
};

export default MessageList;
